/**
 * useCreateMeeting — POST /v1/clinics/{clinic_id}/meetings
 *
 * Explicit-create path for a quarterly review meeting. Most meetings come
 * into existence via lazy creation (first PUT /goal), so the AM rarely
 * calls this directly — the only V1 trigger is the "Schedule next review"
 * footer button.
 *
 * Invalidates the dashboard for the clinic (any year), since the latest
 * meeting feeds the dashboard's `meeting` field.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { MeetingCreateRequest, MeetingResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export function useCreateMeeting(clinicId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MeetingCreateRequest): Promise<MeetingResponse> => {
      const { data } = await getApiClient().post<MeetingResponse>(
        `/v1/clinics/${clinicId}/meetings`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      // Latest meeting may have changed for any year of this clinic.
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.byClinic(clinicId) });
      // The new meeting also needs to appear in the clinic-meetings list.
      void qc.invalidateQueries({ queryKey: queryKeys.clinicMeetings.byClinic(clinicId) });
    },
  });
}
